import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { useLocation } from "wouter";
import { pingVisita } from "./lib/analytics";  // PR #268
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch } from "wouter";
import DashboardNav from "./components/DashboardNav";
import ErrorBoundary from "./components/ErrorBoundary";
import GlobalSearch from "./components/GlobalSearch";
import HealthBadge from "./components/HealthBadge";
import HeaderGlobal from "./components/HeaderGlobal";  // barra de acessos (Usuário/Mediador/Admin) em toda página
import InstallPromptBanner from "./components/InstallPromptBanner";
// OnboardingTour (tour de boas-vindas auto-show) REMOVIDO da landing pública —
// os pop-ups apareciam antes do conteúdo e atrapalhavam. Tours de mediador/admin
// (áreas internas, role-gated) seguem ativos.
import OnboardingTourMediador from "./components/OnboardingTourMediador";
import OnboardingTourAdmin from "./components/OnboardingTourAdmin";
import RequireAdminAuth from "./components/RequireAdminAuth";
import SandboxBanner from "./components/SandboxBanner";
import SupportChat from "./components/SupportChat";
import { ThemeProvider } from "./contexts/ThemeContext";

// Mobile-first (CLAUDE.md §2.1) — code-split por rota. Cada chunk só carrega
// quando o usuário visita a rota, reduzindo dramaticamente o JS inicial.
// `/` é HOST-AWARE: em `empresas.acordei.app.br` a raiz cai direto na Home
// (hero Empresas); nos demais hosts (www / capa) mostra a Entrada (2 cards
// Empresas/Consumidor). A Home (Empresas) também fica em /empresas.
import Entrada from "./pages/Entrada";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import { EMPRESAS_AO_VIVO } from "./lib/launch";

const ConsumidorEmBreve = lazy(() => import("./pages/ConsumidorEmBreve"));
const EmConstrucao = lazy(() => import("./pages/EmConstrucao"));

const CaseFlow = lazy(() => import("./pages/CaseFlow"));
const Result = lazy(() => import("./pages/Result"));
const Tecnologia = lazy(() => import("./pages/Tecnologia"));
const Servicos = lazy(() => import("./pages/Servicos"));
const UsuarioLogin = lazy(() => import("./pages/UsuarioLogin"));  // login do contador (senha)
const UsuarioDashboard = lazy(() => import("./pages/UsuarioDashboard"));  // painel do contador
const FlowMap = lazy(() => import("./pages/FlowMap"));
const FlowMapAdmin = lazy(() => import("./pages/FlowMapAdmin"));
// /admin = hub tributário. O Admin.tsx antigo (painel do produto Consumidor:
// tRPC + setores e-commerce/telecom + Consumidor.gov.br) foi ARQUIVADO — não é
// mais roteado. Mantido no disco pra referência durante a transição.
const AdminHub = lazy(() => import("./pages/AdminHub"));
const Curadoria = lazy(() => import("./pages/Curadoria"));
const AdminErrors = lazy(() => import("./pages/AdminErrors"));
const AdminFunil = lazy(() => import("./pages/AdminFunil"));
const AdminCohort = lazy(() => import("./pages/AdminCohort"));
const FlowMapAI = lazy(() => import("./pages/FlowMapAI"));
// PR #147: imports UserDashboard, AccountSettings, DadosLGPD REMOVIDOS.
// Rotas /minha-conta, /configuracoes, /minha-conta/dados não existem mais.
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const ContratoMediacao = lazy(() => import("./pages/ContratoMediacao"));
const Acompanhar = lazy(() => import("./pages/Acompanhar"));
const Acordo = lazy(() => import("./pages/Acordo"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Blog = lazy(() => import("./pages/Blog"));
const Impacto = lazy(() => import("./pages/Impacto"));
const ParaEmpresas = lazy(() => import("./pages/ParaEmpresas"));
const Sobre = lazy(() => import("./pages/Sobre"));
const Indicar = lazy(() => import("./pages/Indicar"));
const AdminFinanceiro = lazy(() => import("./pages/AdminFinanceiro"));  // PR #103
const AdminValidacao = lazy(() => import("./pages/AdminValidacao"));  // PR #110
const AdminVisaoCompleta = lazy(() => import("./pages/AdminVisaoCompleta"));  // PR #111
const AdminLogin = lazy(() => import("./pages/AdminLogin"));  // PR #113
const Status = lazy(() => import("./pages/Status"));  // PR #118 — status público
const InstalarApp = lazy(() => import("./pages/InstalarApp"));  // PR #135 — instalar APK/PWA
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));  // PR #268 — visitas dashboard
const AdminConfig = lazy(() => import("./pages/AdminConfig"));  // Onda 2 — config de fee + piso
const AdminCadastros = lazy(() => import("./pages/AdminCadastros"));  // Hub — mediadores/cessionária/clientes
const MeuPerfilMediador = lazy(() => import("./pages/MeuPerfilMediador"));  // self-serve perfil do mediador

function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center"
      style={{ background: "oklch(0.09 0.025 240)" }}>
      <div className="text-white/40 text-sm">Carregando…</div>
    </div>
  );
}

/** PR #268: dispara POST /visits/ping em cada mudança de rota. */
function PageviewTracker() {
  const [location] = useLocation();
  useEffect(() => {
    // Skip ping em rotas admin/internas (não distorce métrica de site público)
    if (location.startsWith("/admin")) return;
    void pingVisita(location || "/");
  }, [location]);
  return null;
}

/** Raiz host-aware + gating de lançamento. Em `empresas.acordei.app.br`: cai na
    Home (hero) SE EMPRESAS_AO_VIVO; senão na página de construção (Empresas ainda
    não lançado publicamente). Nos demais hosts (www / capa): Entrada com os 2
    cards. Decisão client-side por hostname, sem flash e sem alterar a URL. */
function RootRoute() {
  const isEmpresasHost =
    typeof window !== "undefined" &&
    window.location.hostname.startsWith("empresas.");
  if (isEmpresasHost) return EMPRESAS_AO_VIVO ? <Home /> : <EmConstrucao />;
  return <Entrada />;
}

function Router() {
  return (
    <Suspense fallback={<PageFallback />}>
      <PageviewTracker />
      <Switch>
        {/* `/` host-aware (ver RootRoute): empresas.acordei.app.br → Home direto;
            www/capa → Entrada (2 cards). A Home (Empresas) também fica em /empresas. */}
        <Route path="/" component={RootRoute} />
        <Route path="/em-construcao" component={EmConstrucao} />
        <Route path="/empresas" component={Home} />
        <Route path="/consumidor" component={ConsumidorEmBreve} />
        <Route path="/caso/:sector" component={CaseFlow} />
        <Route path="/resultado/:caseId" component={Result} />
        <Route path="/tecnologia" component={Tecnologia} />
        <Route path="/servicos" component={Servicos} />
        {/* Usuário/contador — login por senha + dashboard (cadastro + empresas + dados) */}
        <Route path="/entrar">{() => <UsuarioLogin />}</Route>
        <Route path="/captador">{() => <UsuarioLogin papel="captador" />}</Route>
        <Route path="/painel" component={UsuarioDashboard} />
        <Route path="/mapa" component={FlowMap} />
        {/* PR #227: /mapa-empresa é mapa TÉCNICO interno (APIs, DB, infra) —
            só admin. Anteriormente era público — vazava arquitetura. */}
        <Route path="/mapa-empresa">
          <RequireAdminAuth role="admin"><FlowMapAdmin /></RequireAdminAuth>
        </Route>
        {/* Rotas admin envolvidas em RequireAdminAuth: sem login → formulário de
            senha (/admin/login); a role é exigida no backend por endpoint. */}
        <Route path="/admin">
          <RequireAdminAuth role="admin"><AdminHub /></RequireAdminAuth>
        </Route>
        <Route path="/admin/curadoria">
          <RequireAdminAuth role="mediador"><Curadoria /></RequireAdminAuth>
        </Route>
        <Route path="/admin/erros">
          <RequireAdminAuth role="admin"><AdminErrors /></RequireAdminAuth>
        </Route>
        <Route path="/admin/funil">
          <RequireAdminAuth role="admin"><AdminFunil /></RequireAdminAuth>
        </Route>
        <Route path="/admin/cohort">
          <RequireAdminAuth role="admin"><AdminCohort /></RequireAdminAuth>
        </Route>
        {/* PR #103 — relatório financeiro Asaas + fee (env-driven, default 20%) + CSV contábil */}
        <Route path="/admin/financeiro">
          <RequireAdminAuth role="admin"><AdminFinanceiro /></RequireAdminAuth>
        </Route>
        {/* Onda 2 — config de fee + piso (admin edita % e piso, persiste no DB) */}
        <Route path="/admin/config">
          <RequireAdminAuth role="admin"><AdminConfig /></RequireAdminAuth>
        </Route>
        {/* Hub de cadastros — mediadores (ficha), cessionária (PIX), clientes */}
        <Route path="/admin/cadastros">
          <RequireAdminAuth role="admin"><AdminCadastros /></RequireAdminAuth>
        </Route>
        {/* Auto-cadastro do mediador logado (self-serve) */}
        <Route path="/admin/meu-perfil">
          <RequireAdminAuth role="mediador"><MeuPerfilMediador /></RequireAdminAuth>
        </Route>
        {/* PR #110 — painel do mediador (validação simulação) */}
        <Route path="/admin/validacao">
          <RequireAdminAuth role="mediador"><AdminValidacao /></RequireAdminAuth>
        </Route>
        {/* PR #111 — visão completa da simulação (admin) */}
        <Route path="/admin/visao-completa">
          <RequireAdminAuth role="admin"><AdminVisaoCompleta /></RequireAdminAuth>
        </Route>
        {/* PR #268 — analytics de visitas (admin/mediador) */}
        <Route path="/admin/analytics">
          <RequireAdminAuth role="mediador"><AdminAnalytics /></RequireAdminAuth>
        </Route>
        {/* PR #113 — login dedicado (acessível sem auth — é onde user vai pra fazer login) */}
        <Route path="/admin/login" component={AdminLogin} />
        {/* PR #227: /fluxo-ia descreve LLM internals + APIs reguladoras —
            também é uso interno. Admin-only. */}
        <Route path="/fluxo-ia">
          <RequireAdminAuth role="admin"><FlowMapAI /></RequireAdminAuth>
        </Route>
        {/* PR #147 + #150: rotas /minha-conta, /configuracoes, /minha-conta/dados
            REMOVIDAS e components fisicamente deletados — site case-centric,
            cliente sem conta (PR #143). Acesso é apenas via /acompanhar/:protocolo. */}
        <Route path="/termos" component={TermosDeUso} />
        <Route path="/privacidade" component={PoliticaPrivacidade} />
        <Route path="/contrato" component={ContratoMediacao} />
        <Route path="/acompanhar/:protocol" component={Acompanhar} />
        <Route path="/acompanhar" component={Acompanhar} />
        {/* /automacoes (painel consumidor.gov.br) removido — era do produto
            Consumidor; o admin tributário não usa. */}
        <Route path="/acordo/:caseId" component={Acordo} />
        <Route path="/faq" component={FAQ} />
        <Route path="/blog" component={Blog} />
        <Route path="/impacto" component={Impacto} />
        <Route path="/para-empresas" component={ParaEmpresas} />
        <Route path="/sobre" component={Sobre} />
        <Route path="/indicar" component={Indicar} />
        {/* PR #118 — status público sem auth */}
        <Route path="/status" component={Status} />
        {/* PR #135 — página /instalar com link APK + instruções PWA */}
        <Route path="/instalar" component={InstalarApp} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark" switchable>
        <TooltipProvider>
          {/* PR #163: skip link a11y — primeira coisa que screen reader anuncia.
              Visível apenas com :focus (keyboard nav). Pula nav repetitivo. */}
          <a
            href="#conteudo-principal"
            className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-cyan-500 focus:text-black focus:px-4 focus:py-2 focus:rounded-lg focus:font-semibold"
          >
            Pular para o conteúdo principal
          </a>
          <Toaster />
          {/* PR #100: tours específicos por role — cada um auto-detecta rota e
              só aparece pra quem está naquela área. STORAGE_KEY próprio.
              O tour genérico de boas-vindas (público) foi removido: aparecia
              como pop-ups antes da landing. */}
          <OnboardingTourMediador />
          <OnboardingTourAdmin />
          <SupportChat />
          <GlobalSearch />
          <HealthBadge />
          {/* PR #98: banner sandbox no topo de rotas internas. Sai se ACORDEI_SANDBOX_MODE
              estiver OFF no backend ou se user fechar manualmente. */}
          <SandboxBanner />
          {/* PR #94: navegação global entre 3 painéis (Minha Conta / Mediador / Admin).
              Aparece em rotas internas, escondida em landing públicas. */}
          <DashboardNav />
          {/* PR #90: banner de install PWA — DESABILITADO em PR #137 enquanto APK
              não está distribuído oficialmente na Play Store. Reativar quando subir. */}
          {/* <InstallPromptBanner /> */}
          {/* Barra de acessos global (Usuário/Mediador/Admin) — sempre no topo,
              em qualquer página, a pedido do Arthur. */}
          <HeaderGlobal />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
